import { ValidationError } from "@core/domain/errors/validation.error";
import { Pagination } from "@core/domain/models/pagination";
import { Either } from "@core/domain/types/either";
import { EntityDatasource } from "@entity/data/datasources/entity.datasource";
import { ActionLine } from "@entity/domain/models/action-line.model";
import { AdministrativeRecordScope } from "@entity/domain/models/administrative-record-scope.model";
import { Category } from "@entity/domain/models/category.model";
import { CrosscuttingScope } from "@entity/domain/models/crosscutting-scope.model";
import { EntityTerritorialScope } from "@entity/domain/models/entity-territorial-scope.model";
import { ExportProjectDeposits } from "@entity/domain/models/export-project-deposits.model";
import { ExportProjectExpenses } from "@entity/domain/models/export-project-expenses.model";
import { Typology } from "@entity/domain/models/typology.model";
import { inject, injectable } from "inversify";
import { CreateEntity } from "../../domain/models/create-entity.model";
import { CreateUserEntity } from "../../domain/models/create-lite-entity.model";
import { EditEntity } from "../../domain/models/edit-entity.model";
import {
    EntitiesSummary,
    EntitySearchFilters,
    EntitySummary,
} from "../../domain/models/entity-summary.model";
import { Entity } from "../../domain/models/entity.model";
import { Materials } from "../../domain/models/material/material.model";
import { EntityError } from "@entity/domain/errors/entity.error";
import { EntityMapperError } from "@entity/domain/errors/entity/entity-mapper.error";

@injectable()
export class EntityRepository {
    constructor(
        @inject(EntityDatasource)
        private readonly entityDatasource: EntityDatasource,
    ) { }

    async getBy(
        pagination: Pagination,
        filters?: EntitySearchFilters,
    ): Promise<Either<EntityError, EntitiesSummary>> {
        const entitiesSummary = await this.entityDatasource.fetchBy(
            pagination,
            filters,
        );

        return entitiesSummary.mapLeft(() => new EntityError("getEntitiesError"));
    }

    async getAllBy(
        filters?: EntitySearchFilters,
    ): Promise<Either<EntityError, EntitySummary[]>> {
        const entitiesResult = await this.getBy(
            Pagination.NoPagination(),
            filters,
        );

        return entitiesResult.map((entities) => entities.entitiesSummary);
    }

    async getAllCategories(): Promise<Either<EntityError, Category[]>> {
        const categoriesResult =
            await this.entityDatasource.fetchAllCategories();

        return categoriesResult
            .mapLeft(() => new EntityError("getCategoriesError"))
            .map((category) => category);
    }

    async getMaterials(
        pagination: Pagination,
    ): Promise<Either<EntityError, Materials>> {
        const materialResult =
            await this.entityDatasource.fetchMaterials(pagination);

        return materialResult.mapLeft(() => new EntityError("fetchMaterialsError"));
    }

    async create(
        createEntity: CreateEntity | CreateUserEntity,
    ): Promise<Either<ValidationError | EntityError | EntityMapperError, Entity>> {
        return this.entityDatasource.create(createEntity);
    }

    async update(
        entity: EditEntity,
    ): Promise<Either<ValidationError | EntityError | EntityMapperError, Entity>> {
        return this.entityDatasource.update(entity);
    }

    async getById(id: number): Promise<Either<EntityError | EntityMapperError, Entity>> {
        const entityResult = await this.entityDatasource.fetchById(id);

        if (entityResult.isLeft()) {
            return Either.Left(entityResult.getLeftOrThrow());
        }

        const entity = entityResult.getOrThrow().entity;

        return Either.Right(entity);
    }

    async getAllTypologies(): Promise<Either<EntityError, Typology[]>> {
        return this.entityDatasource.fetchTypologies(Pagination.NoPagination());
    }

    async getAllActionLines(): Promise<Either<EntityError, ActionLine[]>> {
        return this.entityDatasource.fetchActionLines(
            Pagination.NoPagination(),
        );
    }

    async getAllCrosscuttingScopes(): Promise<
        Either<EntityError, CrosscuttingScope[]>
    > {
        return this.entityDatasource.fetchCrosscuttingScopes(
            Pagination.NoPagination(),
        );
    }

    async getAllAdministrativeRecordScopes(): Promise<
        Either<EntityError, AdministrativeRecordScope[]>
    > {
        return this.entityDatasource.fetchAllAdministrativeRecordScopes();
    }

    async getAllEntityTerritorialScopes(): Promise<
        Either<EntityError, EntityTerritorialScope[]>
    > {
        return this.entityDatasource.fetchAllEntityTerritorialScopes();
    }

    async active(entityId: number): Promise<Either<EntityError, true>> {
        return this.entityDatasource.toggleActivation(entityId);
    }

    async deactivate(entityId: number): Promise<Either<EntityError, true>> {
        return this.entityDatasource.toggleActivation(entityId);
    }

    async exportProjectDeposit(
        exportProjectDeposits: ExportProjectDeposits,
    ): Promise<Either<EntityError, Blob>> {
        return this.entityDatasource.exportProjectDeposit(
            exportProjectDeposits,
        );
    }

    async exportProjectExpenses(
        exportProjectExpenses: ExportProjectExpenses,
    ): Promise<Either<EntityError, Blob>> {
        return this.entityDatasource.exportProjectExpenses(
            exportProjectExpenses,
        );
    }

    async delete(entityId: number): Promise<Either<EntityError, true>> {
        return this.entityDatasource.delete(entityId);
    }
}
